import { useNavigateWithReturnState } from 'utils/useNavigateWithReturnState';
import ListCreateButton from 'components/buttons/ListCreateButton';
import { AssignmentIndOutlined as AssignmentIndOutlinedIcon } from 'components/icons';
import type { DataTableColumnDef } from 'components/tables/data-table';
import ListTableWithFilters from 'components/tables/list-table/ListTableWithFilters';
import { useMemo } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { getClassifierText, t } from 'utils/getTranslations';

import { FilterConfig } from 'components/tables/list-table/ListTableTypes';
import { DraftNameCell } from 'components/drafts/DraftNameCell';
import { useWithDrafts } from 'components/drafts/useWithDrafts';
import { useLazyGetJobRolesWithFiltersQuery } from 'store/api/jobRoles';
import { JobRolesListElement } from 'types/jobRoles';
import { useClassifiers } from 'utils/useClassifiers';

const USED_CLASSIFIERS = ['JOB_ROLE_GROUP_TYPE'];

const getJobRoleHref = (jobRole: JobRolesListElement) => `/job-role/${jobRole.id}`;

const JobRolesTable: React.FC<Record<string, never>> = () => {
    const navigateWithReturn = useNavigateWithReturnState();
    const [fetchJobRoles, { data, isFetching }] = useLazyGetJobRolesWithFiltersQuery();
    const fetchData = useWithDrafts(fetchJobRoles);
    // Rows are not gated on the classifier fetch: getClassifierText falls back to the raw code,
    // so the Category cell degrades to LEGAL/ADMIN instead of holding the whole table back.
    const { classifiers } = useClassifiers({ codes: USED_CLASSIFIERS });

    const columns = useMemo<DataTableColumnDef<JobRolesListElement>[]>(
        () => [
            {
                header: t('public-name'),
                id: 'name',
                accessorKey: 'name',
                meta: {
                    widthWeight: 1,
                    minPx: 160,
                },
                cell: ({ cell }) => {
                    const { id, name, isDraft } = cell.row.original;
                    return id ? (
                        <DraftNameCell draft={isDraft ? { type: 'job-role', id } : null}>
                            <RouterLink className="entity-link font-semibold hover:underline" to={`/job-role/${id}`}>
                                {name || (isDraft ? t('new-entity.job-role') : '-')}
                            </RouterLink>
                        </DraftNameCell>
                    ) : (
                        '-'
                    );
                },
            },
            {
                header: t('job-roles.internal-name'),
                id: 'internalName',
                accessorKey: 'internalName',
                meta: {
                    widthWeight: 1,
                    minPx: 150,
                },
                cell: ({ getValue }) => getValue<JobRolesListElement['internalName']>() ?? '',
            },
            {
                header: t('job-roles.abbreviation'),
                id: 'abbreviation',
                accessorKey: 'abbreviation',
                meta: {
                    widthWeight: 1,
                    minPx: 110,
                },
                cell: ({ getValue }) => getValue<JobRolesListElement['abbreviation']>() ?? '',
            },
            {
                header: t('order-number'),
                id: 'orderNum',
                accessorKey: 'orderNum',
                meta: {
                    widthWeight: 1,
                    minPx: 100,
                },
                cell: ({ getValue }) => getValue<JobRolesListElement['orderNum']>() ?? '',
            },
            {
                header: t('category'),
                id: 'groupNameTypeCode',
                accessorKey: 'groupNameTypeCode',
                meta: {
                    widthWeight: 1,
                    minPx: 130,
                },
                cell: ({ getValue }) => {
                    const value = getValue<JobRolesListElement['groupNameTypeCode']>();
                    return value ? getClassifierText(classifiers['JOB_ROLE_GROUP_TYPE'], value) : '';
                },
            },
            {
                header: t('job-roles.excl-from-minmax-calculation'),
                id: 'excludeFromMinmaxCalculation',
                accessorKey: 'excludeFromMinmaxCalculation',
                meta: {
                    widthWeight: 1,
                    minPx: 160,
                },
                cell: ({ getValue }) =>
                    getValue<JobRolesListElement['excludeFromMinmaxCalculation']>() ? t('yes') : t('no'),
            },
        ],
        [classifiers]
    );

    const filterConfig: FilterConfig = useMemo(
        () => ({
            quickFilters: [],
            filters: [],
            searchBarSelectors: [{ selector: 'name' }, { selector: 'internalName' }, { selector: 'orderNum' }],
        }),
        []
    );

    return (
        <ListTableWithFilters<JobRolesListElement>
            getRowHref={getJobRoleHref}
            // Managed v8 engine for the weighted-width layout; column picker / reorder / resize
            // stay OFF (flags default false) — this table never had them.
            columnConfigId="job-roles"
            columns={columns}
            data={data}
            isLoading={isFetching}
            pluralTextId="job-roles.job-roles"
            singularTextId="job-roles.job-role"
            noEntriesId="job-roles.no-job-roles-found"
            noEntriesBodyId="job-roles.empty.body"
            noEntriesIcon={AssignmentIndOutlinedIcon}
            noEntriesAction={
                <ListCreateButton
                    permissions={['CREATE.JOB_ROLES']}
                    variant="contained"
                    color="secondary"
                    onClick={() => navigateWithReturn('/job-role/new')}
                    labelId="job-roles.add-new-job-role"
                    newLabelId="new-entity.job-role"
                />
            }
            fetchData={fetchData}
            filterConfig={filterConfig}
            rowsPerPage={50}
        />
    );
};

export default JobRolesTable;
